import { useState } from "react";
import { Form, Formik } from "formik";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { PropagateLoader } from "react-spinners";
import Button from "../../atoms/Button";
import RentaSection from "../../molecules/inmueblesCreate/RentaSection";
import HipotecaSection from "../../molecules/inmueblesCreate/HipotecaSection";
import DocumentacionSection from "../../molecules/inmueblesCreate/DocumentacionSection";
import DatosGeneralesSection from "../../molecules/inmueblesCreate/DatosGeneralesSection";
import { initialValuesInmueble, validationSchemaInmueble } from "../../molecules/inmueblesCreate/formSchemaInmueble";
import { REGISTER_USER } from "../../../api/apiUsers";
import { REGISTER_EDO_CTA } from "../../../api/apiEdoCuenta";
import { REGISTER_INMUEBLE } from "../../../api/apiInmuebles";
import { REGISTER_ARCHIVO_PERMANTENTE, REGISTER_HIPOTECA, REGISTER_RENTA } from "../../../api/apiFiles";
import { getMonthsBetweenDates } from "../../../utils/dateUtils";
import AirbnbSection from "../../molecules/inmueblesCreate/AirbnbSection";
import { REGISTER_AIRBNB } from "../../../api/apiAirbnb";

const InmueblesCreate = ({ refetch, onClose}) => {
    const { id } = useParams();
    const [isAval, setIsAval] = useState(true);
    const [archivosPermanentes, setArchivosPermanentes] = useState({
        escritura: null,
        comprobante_domicilio: null,
        predial: null,
        seguro: null,
        contrato_luz: null,
        contrato_agua: null,
    });
    const [archivosHipoteca, setArchivosHipoteca] = useState({
        compra_venta: null,
        amortizacion: null,
        seguro_mortgage: null,
    });
    const [archivosRenta, setArchivosRenta] = useState({
        contrato_renta: null,
		ine_inquilino: null,
		datos_facturacion: null,
		poliza_aval: null,
    });

    const handleUploadAP = async (inmuebleId, archivosPermanentes) => {
        try {
            await REGISTER_ARCHIVO_PERMANTENTE(inmuebleId, archivosPermanentes);
        } catch (error) {
            console.error(error);
            toast.error("Error al registrar el archivo permanente");
        }
    };

    const handleUploadHipoteca = async (inmuebleId, archivosHipoteca, values) => {
        try {
            await REGISTER_HIPOTECA(inmuebleId, archivosHipoteca, values);
        } catch (error) {
            console.error(error);
            toast.error("Error al registrar la hipoteca");
        }
    };

    const handleUploadRenta = async (inmuebleId, archivosRenta, values) => {
        try {
            const datos_facturacion = {
                razonSocial: values.fac_razonSocial,
                rfc: values.fac_rfc,
                usoCFDI: values.fac_usoCFDI,
                email: values.fac_email
            };

            await REGISTER_RENTA(inmuebleId, archivosRenta, {...values, datos_facturacion: JSON.stringify(datos_facturacion)});
        } catch (error) {
            console.error(error);
            toast.error("Error al registrar la renta");
        }
    };

    const handleRegisterEdoCtaMonthly = async ({ propertieId, userId, total, iva, subtotal, date_start, date_end }) => {
        const months = getMonthsBetweenDates(date_start, date_end);

        for (const { from, to } of months) {
            const monthLabel = `${from} - ${to}`;

            const newEdoCuenta = {
                propertieId,
                userId,
                total,
                iva,
                subtotal,
                month: monthLabel,
                status: false,
            };

            try {
                await REGISTER_EDO_CTA(newEdoCuenta);
            } catch (error) {
                console.error("Error registrando estado de cuenta mensual:", error);
            }
        }
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            let idClient = null;
            if (values.isRented === true && !values.userId) {
                try {
                    const response = await REGISTER_USER({ email:values.userEmail, name:values.userName, role:"Resident", password:"inquilino" });
                    idClient = response.user.user.id;
                } catch (error) {
                    console.error(error);
                }
            };

            const response = await REGISTER_INMUEBLE(values.userId ? values : {...values, userId: idClient});
            const inmuebleId = response.id;

            if (inmuebleId) {
                await handleUploadAP(inmuebleId, archivosPermanentes);
                if(values.isMortgaged === true) {
                    await handleUploadHipoteca(inmuebleId, archivosHipoteca, values);
                }
                if(values.isRented === true) {
                    await handleUploadRenta(inmuebleId, archivosRenta, values.userId ? values : {...values, userId:idClient});
                }
                if(values.isRented) {
                    await handleRegisterEdoCtaMonthly({
                        propertieId: inmuebleId,
                        userId: values.userId || idClient,
                        total: values.monto,
                        iva: values.iva,
                        subtotal: values.subtotal,
                        date_start: values.date_start,
                        date_end: values.date_end
                    });
                }
                if (values.type === "Airbnb") {
                    await REGISTER_AIRBNB(inmuebleId, values.airbnbDocuments);
                }
            }
            toast.success("Agregado correctamente");
            refetch();
            onClose();
        } catch (error) {
            console.error(error);
            toast.error("Error al registrar el inmueble");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Formik initialValues={{ ...initialValuesInmueble, real_estate: Number(id) }} onSubmit={handleSubmit} validationSchema={validationSchemaInmueble}>
            {({ values, isSubmitting, setFieldValue }) => (
                <Form className="flex flex-col justify-center items-center gap-5">
                    <DatosGeneralesSection/>
                    <DocumentacionSection setFieldValue={setFieldValue} setArchivosPermanentes={setArchivosPermanentes} />
                    <HipotecaSection values={values} setFieldValue={setFieldValue} setArchivosHipoteca={setArchivosHipoteca} />
                    <RentaSection values={values} setFieldValue={setFieldValue} setArchivosRenta={setArchivosRenta} isAval={isAval} setIsAval={setIsAval} />
                    {values.type === "Airbnb" &&
                        <AirbnbSection values={values} setFieldValue={setFieldValue} />
                    }
                    <Button text={isSubmitting ? <PropagateLoader color="red" /> : "Agregar"} type="submit" bgColor="blue" disabled={isSubmitting}/>
                </Form>
            )}
        </Formik>
    );
};

export default InmueblesCreate;